/**
 * Plantillas por defecto para certificados de instalación.
 * Texto plano (sin HTML). Saltos de línea con \n.
 *
 * Reutilizamos `sanitizarTextoContrato` de contratos para no duplicar lógica.
 */

export { sanitizarTextoContrato } from '@/lib/contratos/plantillasPorDefecto';

export interface ClausulaCertificado {
  titulo: string;
  contenido: string;
}

export interface PlantillaCertificado {
  tipo: 'seguridad' | 'pci';
  nombre: string;
  objeto: string;
  clausulas: ClausulaCertificado[];
  texto_legal: string;
}

export const PLANTILLA_CERT_SEGURIDAD: PlantillaCertificado = {
  tipo: 'seguridad',
  nombre: 'Certificado de instalación de sistemas de seguridad',
  objeto:
    'El presente certificado acredita la correcta instalación y puesta en servicio de los sistemas de seguridad relacionados en las dependencias del CLIENTE, ejecutados por la empresa instaladora autorizada, conforme a la Ley 5/2014, de 4 de abril, de Seguridad Privada, al Reglamento de Seguridad Privada (RD 2364/1994) y, cuando los sistemas instalados sean de intrusión, a la norma UNE-EN 50131-1 al grado de seguridad declarado en el presente documento.',
  clausulas: [
    {
      titulo: '1. Empresa instaladora',
      contenido:
        'La empresa instaladora figura inscrita en el Registro Nacional de Empresas de Seguridad de la Dirección General de la Policía con el número que consta en el encabezado de este documento. La instalación ha sido ejecutada y supervisada por personal cualificado de dicha empresa.',
    },
    {
      titulo: '2. Sistemas instalados',
      contenido:
        'Se ha procedido a la instalación de los sistemas relacionados en el apartado "Sistemas incluidos" del presente certificado, en las ubicaciones indicadas. Para los sistemas de intrusión, la instalación se ha diseñado y ejecutado conforme al grado UNE-EN 50131-1 declarado, valorando el nivel de riesgo, la zona protegida y las prestaciones requeridas por el titular.',
    },
    {
      titulo: '3. Pruebas y comprobaciones realizadas',
      contenido:
        'Se han realizado las pruebas funcionales sobre todos los elementos instalados (centrales, detectores, sirenas, comunicadores, cámaras, lectores, etc.), comprobando su correcto funcionamiento, la transmisión de señales a la Central Receptora de Alarmas (cuando proceda) y la integridad de las comunicaciones.',
    },
    {
      titulo: '4. Documentación entregada al titular',
      contenido:
        'Se hace entrega al CLIENTE de los manuales de usuario, planos de instalación, claves de programación reservadas al titular y el resto de documentación técnica necesaria para el correcto uso del sistema.',
    },
    {
      titulo: '5. Garantía',
      contenido:
        'Los equipos y la mano de obra están cubiertos por la garantía legalmente establecida y por la garantía comercial ofrecida por el PRESTADOR. Quedan excluidas las averías derivadas de un uso indebido, manipulación por terceros no autorizados, sobretensiones o fenómenos atmosféricos.',
    },
    {
      titulo: '6. Obligaciones del titular',
      contenido:
        'El CLIENTE queda informado de la obligación de mantener el sistema en correcto estado de funcionamiento conforme al artículo 41 del RD 2364/1994, suscribiendo el correspondiente contrato de mantenimiento con empresa autorizada y conservando el libro/registro de mantenimiento a disposición de la autoridad competente.',
    },
    {
      titulo: '7. Protección de datos',
      contenido:
        'Ambas partes se comprometen a tratar los datos personales conforme al Reglamento (UE) 2016/679 (RGPD) y la LOPDGDD 3/2018. Cuando la instalación incluya videovigilancia, el CLIENTE asume la condición de Responsable del Tratamiento y se compromete a señalizar las zonas videovigiladas conforme a la normativa vigente.',
    },
  ],
  texto_legal:
    'Empresa instaladora de sistemas de seguridad inscrita en el Registro Nacional de Empresas de Seguridad del Ministerio del Interior. Este certificado se emite al amparo de la Ley 5/2014 de Seguridad Privada, el RD 2364/1994 y, en su caso, la norma UNE-EN 50131-1 (grados 1 a 4).',
};

export const PLANTILLA_CERT_PCI: PlantillaCertificado = {
  tipo: 'pci',
  nombre: 'Certificado de instalación de protección contra incendios',
  objeto:
    'El presente certificado acredita la correcta instalación de los sistemas de protección contra incendios (PCI) relacionados en las dependencias del CLIENTE, ejecutados por empresa instaladora habilitada, conforme al Reglamento de Instalaciones de Protección Contra Incendios (RD 513/2017, de 22 de mayo) y demás normativa aplicable.',
  clausulas: [
    {
      titulo: '1. Empresa instaladora',
      contenido:
        'La empresa instaladora se encuentra habilitada para la instalación de sistemas de protección contra incendios conforme al RD 513/2017 y figura inscrita en el registro del órgano competente de la Comunidad Autónoma correspondiente.',
    },
    {
      titulo: '2. Sistemas instalados',
      contenido:
        'Se han instalado los sistemas relacionados en el apartado "Sistemas incluidos" del presente certificado (detección y alarma, extintores, BIEs, hidrantes, exutorios, puertas cortafuegos, extinción automática, alumbrado de emergencia, etc.), cumpliendo las normas UNE de aplicación a cada sistema.',
    },
    {
      titulo: '3. Pruebas funcionales',
      contenido:
        'Se han realizado las pruebas funcionales preceptivas sobre los sistemas instalados, comprobando su correcto funcionamiento, presiones, autonomía y, en su caso, integración con la central de detección y alarma.',
    },
    {
      titulo: '4. Documentación entregada',
      contenido:
        'Se entrega al CLIENTE la documentación técnica de la instalación: memoria, planos, certificados de los productos (marcado CE / declaraciones de prestaciones), manuales de uso y mantenimiento, e instrucciones de actuación en caso de emergencia.',
    },
    {
      titulo: '5. Mantenimiento obligatorio',
      contenido:
        'Conforme al Anexo II del RD 513/2017, los sistemas instalados quedan sometidos a las operaciones de mantenimiento periódico (trimestrales, semestrales, anuales y quinquenales) que deberán ser realizadas por empresa mantenedora habilitada. El CLIENTE se obliga a suscribir y mantener vigente el correspondiente contrato de mantenimiento.',
    },
    {
      titulo: '6. Garantía',
      contenido:
        'Los equipos suministrados y la mano de obra de la instalación están cubiertos por la garantía legal y por la garantía comercial ofrecida por el PRESTADOR. Quedan excluidas las averías derivadas de uso indebido, manipulación, vandalismo o agentes externos.',
    },
  ],
  texto_legal:
    'Empresa instaladora de sistemas de protección contra incendios habilitada conforme al RD 513/2017. Este certificado se emite al amparo del citado Reglamento, así como de las normas UNE aplicables a cada sistema (UNE 23007, UNE-EN 671, UNE 23500, UNE 23510, etc.).',
};

export function plantillaCertificadoPorDefecto(tipo: 'seguridad' | 'pci'): PlantillaCertificado {
  return tipo === 'seguridad' ? PLANTILLA_CERT_SEGURIDAD : PLANTILLA_CERT_PCI;
}

export const GRADOS_UNE_50131: Record<1 | 2 | 3 | 4, { titulo: string; descripcion: string }> = {
  1: {
    titulo: 'Grado 1 — Riesgo bajo',
    descripcion:
      'Instalaciones con bajo nivel de riesgo. Intrusos con escaso conocimiento del sistema y herramientas limitadas. Típicamente viviendas y pequeños comercios sin valores especialmente atractivos.',
  },
  2: {
    titulo: 'Grado 2 — Riesgo bajo a medio',
    descripcion:
      'Intrusos con conocimientos limitados del sistema pero que pueden usar herramientas comunes y dispositivos portátiles. Típicamente comercios, oficinas y viviendas con bienes de valor moderado.',
  },
  3: {
    titulo: 'Grado 3 — Riesgo medio a alto',
    descripcion:
      'Intrusos con conocimiento del sistema, herramientas avanzadas y equipos electrónicos portátiles. Aplicable a establecimientos con obligación reglamentaria de conexión a CRA (joyerías, farmacias, estaciones de servicio, etc.).',
  },
  4: {
    titulo: 'Grado 4 — Riesgo alto',
    descripcion:
      'Intrusos con planificación detallada del ataque, recursos sustanciales y dominio del sistema de seguridad. Aplicable a instalaciones de alta seguridad (entidades de crédito, infraestructuras críticas, instalaciones reguladas).',
  },
};
